import { Box, Table, TableBody, TableHead, TableRow, Typography } from '@mui/material';
import {
  DialogBody,
  DialogContainer,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../../common/components';
import { formatCurrency, formatNumber } from '../../../../utils/formatUtils';
import StatisticsDialogTitle from '../common/StatisticsDialogTitle';
import { useExcelExport } from '../../hooks/useExcelExport';
import { useCollectingEntitiesDetailPDF } from '../../hooks/useCollectingEntitiesDetailPDF';
import { COLLECTING_ENTITIES_DETAIL_COLUMNS } from '../../constants/collectingEntitiesConstants';

const DETAIL_FILENAME = 'DetalleEntidadesRecaudadoras';

const formatDetailCellValue = (column, value) => {
  if (column.kind === 'amount') {
    return formatCurrency(value, 0, { fallback: '$ 0' });
  }

  if (column.kind === 'quantity') {
    return formatNumber(value, { decimals: 2, fallback: '0,00' });
  }

  if (value === null || value === undefined || value === '') {
    return '-';
  }

  return value;
};

const buildSummarySheet = (header = {}, summary = {}) => [
  {
    Constructora: header.constructora_info || '-',
    Actualizado: header.updated_at_date || '-',
    Plan: header.plan_name || '-',
    Entidad: header.legal_entity_name || '-',
    Grupo: header.group_name || '-',
    Periodo: header.date_filter_text || '-',
    Registros: Number(summary?.total_records ?? 0),
    'Intereses totales': Number(summary?.total_interest ?? 0),
    'Importe total': Number(summary?.total_amount ?? 0),
  },
];

const buildDetailSheet = (results = []) =>
  results.map((row) =>
    COLLECTING_ENTITIES_DETAIL_COLUMNS.reduce((accumulator, column) => {
      accumulator[column.label] =
        column.kind === 'amount' || column.kind === 'quantity'
          ? Number(row?.[column.key] ?? 0)
          : (row?.[column.key] ?? '-');
      return accumulator;
    }, {})
  );

const CollectingEntitiesDetailModal = ({
  open,
  handleClose,
  header = {},
  results = [],
  summary = null,
}) => {
  const { exportMultipleSheets } = useExcelExport();
  const { exportCollectingEntitiesDetailPDF } = useCollectingEntitiesDetailPDF();
  const hasData = Array.isArray(results) && results.length > 0;

  const handleExportExcel = () => {
    if (!hasData) {
      return;
    }

    exportMultipleSheets(
      [
        { name: 'Resumen', data: buildSummarySheet(header, summary) },
        { name: 'Detalle', data: buildDetailSheet(results) },
      ],
      DETAIL_FILENAME
    );
  };

  const handleExportPDF = () => {
    if (!hasData) {
      return;
    }

    exportCollectingEntitiesDetailPDF({
      title: 'Detalle de entidades recaudadoras',
      filename: DETAIL_FILENAME,
      header,
      summary,
      results,
    });
  };

  return (
    <DialogContainer open={open} onClose={handleClose} size="fullWidth">
      <StatisticsDialogTitle
        title="Detalle de entidades recaudadoras"
        onExportExcel={handleExportExcel}
        onExportPDF={handleExportPDF}
        onClose={handleClose}
        showExcelButton={hasData}
        showPdfButton={hasData}
      />
      <DialogBody>
        <Box
          sx={{
            mb: 3,
            textAlign: 'center',
            border: '1px solid #4a90e2',
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
            {header.constructora_info || 'Constructora Colonial S.R.L.'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic', mb: 0.5 }}>
            Actualizado al: {header.updated_at_date || '-'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555' }}>
            {header.date_filter_text || 'Sin filtros de fecha'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', mt: 0.5 }}>
            Plan: {header.plan_name || '-'} | Entidad: {header.legal_entity_name || '-'} | Grupo:{' '}
            {header.group_name || '-'}
          </Typography>
        </Box>

        {summary && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            {[
              {
                label: 'Registros',
                value: formatNumber(summary.total_records, { decimals: 0, fallback: '0' }),
              },
              {
                label: 'Intereses acumulados',
                value: formatCurrency(summary.total_interest, 0, { fallback: '$ 0' }),
              },
              {
                label: 'Importe acumulado',
                value: formatCurrency(summary.total_amount, 0, { fallback: '$ 0' }),
              },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  flex: '1 1 220px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: '#f9fafc',
                }}
              >
                <Typography variant="subtitle2" sx={{ color: '#555' }}>
                  {item.label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {!hasData ? (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            No se encontraron resultados para los filtros seleccionados.
          </Typography>
        ) : (
          <GeneralTableBox sx={{ minHeight: 'auto', maxHeight: '70vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {COLLECTING_ENTITIES_DETAIL_COLUMNS.map((column) => (
                    <HeadTableCell
                      key={column.key}
                      align={column.align}
                      sx={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </HeadTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((row) => (
                  <TableRow key={row.coupon_uuid} hover>
                    {COLLECTING_ENTITIES_DETAIL_COLUMNS.map((column) => (
                      <RowTableCell key={`${row.coupon_uuid}-${column.key}`} align={column.align}>
                        {formatDetailCellValue(column, row[column.key])}
                      </RowTableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </GeneralTableBox>
        )}
      </DialogBody>
    </DialogContainer>
  );
};

export default CollectingEntitiesDetailModal;
